import React, {
  useMemo,
  useContext,
  createContext
} from 'react';
import {
  node, string, func, arrayOf, shape
} from 'prop-types';
import {
  useStoreId,
  useStore,
  ExperienceContext
} from '@thd-nucleus/experience-context';
import { useDataModel } from '@thd-nucleus/data-sources';
import { CompatibleAccessoriesLoader } from './CompatibleAccessoriesLoader';
import { dataModel } from '../dataModel';
import { getModelInfo, getAccessoryId } from '../utils';
import { DEFAULT_STORE_ZIP, EDGE_CASES_TO_IGNORE } from '../constants';

const CompatibleAccessoriesContext = createContext({
  parentCategory: '',
  modelNumber: '',
  accessoryItemId: '',
  parent: '',
  handleChecked: null,
  hasCheckbox: false,
  storeId: '',
  zipCode: '',
  channel: ''
});
export const useCompatibleAccessories = () => useContext(CompatibleAccessoriesContext);

export const CompatibleAccessoriesProvider = ({
  children, itemId, parent, handleChecked, lineItemId, matchIdsForCheckbox
}) => {
  const { channel } = useContext(ExperienceContext);
  const storeId = useStoreId();
  const store = useStore();
  const { storeZip } = store;

  const {
    data: productData,
    loading: productLoading,
    error: productError
  } = useDataModel('product', {
    variables: {
      itemId,
      storeId,
      zipCode: storeZip || DEFAULT_STORE_ZIP,
      channel: channel.toUpperCase()
    },
    ssr: true,
    skip: !storeId
  });
  const { parentCategory, modelNumber } = getModelInfo({ data: productData });
  const hasValidModelNumber = !!modelNumber?.length && !EDGE_CASES_TO_IGNORE.includes(modelNumber);

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError
  } = useDataModel('searchModel', {
    variables: {
      keyword: modelNumber,
      storeId,
      channel: channel.toUpperCase()
    },
    ssr: false,
    skip: !hasValidModelNumber
  });
  const accessoryItemId = getAccessoryId({ data: searchData, modelNumber });
  const hasAccessoryItemId = !!accessoryItemId?.length;
  const checkedState = parent === 'PAS'
    ? matchIdsForCheckbox.includes(accessoryItemId)
    : null;

  const providerValue = useMemo(() => ({
    modelNumber,
    parentCategory,
    accessoryItemId,
    parent,
    handleChecked,
    hasCheckbox: !!handleChecked,
    lineItemId,
    checkedState,
    storeId,
    zipCode: storeZip,
    channel,
    products: searchData?.searchModel?.products
  }), [
    modelNumber,
    parentCategory,
    accessoryItemId,
    itemId,
    lineItemId,
    checkedState,
    parent,
    handleChecked,
    storeId,
    storeZip,
    channel
  ]);

  if ((!(productLoading && searchLoading) && !!(productError && searchError))
    || !hasValidModelNumber
    || !hasAccessoryItemId) {
    return null;
  }
  if (productLoading || searchLoading) {
    return <CompatibleAccessoriesLoader />;
  }

  return (
    <CompatibleAccessoriesContext.Provider value={providerValue}>
      {children}
    </CompatibleAccessoriesContext.Provider>
  );
};

CompatibleAccessoriesProvider.propTypes = {
  children: node.isRequired,
  itemId: string.isRequired,
  parent: string.isRequired,
  handleChecked: func,
  lineItemId: string,
  matchIdsForCheckbox: arrayOf(shape({}))
};

CompatibleAccessoriesProvider.defaultProps = {
  handleChecked: null,
  lineItemId: '',
  matchIdsForCheckbox: []
};

CompatibleAccessoriesProvider.dataModel = dataModel;
